import { baseAxiosInstance } from '../../config/baseAxiosInstance';
import { SignInProps, SignUpProps } from '../../pages/auth/LoginPage/types';
import axiosInstance from '../../config/axiosInstance';
import { localStorageService } from '../localStorageService/localStorageService';
import { AUTH_API_ENDPOINTS } from './endpoints';
import { AuthDeliveryChannel } from '@shared/enums/auth-delivery-channel.enum';
import {
  SetTokensType,
  AuthCodeDeliveryResult,
  InitPhoneSignInResponse,
  SignUpResponse,
  PendingSignUpResponse,
} from './types';
import {
  CompleteSignUpResponse,
  LoginResponse,
} from '../../components/forms/LoginForm/types';
import { isPendingUserResponse } from '../../common/utils/authHelpers';

export class AuthService {
  private readonly endpoints = AUTH_API_ENDPOINTS;

  private setTokens({ accessToken, refreshToken, userId }: SetTokensType) {
    localStorageService().setAccessToken(accessToken);
    localStorageService().setRefreshToken(refreshToken);
    localStorageService().setUserId(userId);
  }

  /** Persist tokens from an auth response, normalizing the user-id field which
   * is `userId` on sign-up/biometric responses but `id` on sign-in responses. */
  private extractAndSetTokens(response: {
    accessToken: string;
    refreshToken: string;
    userId?: string;
    id?: string;
  }) {
    this.setTokens({
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      userId: (response.userId ?? response.id) as string,
    });
  }

  async updateUserBiometricStatus(value: boolean) {
    return await axiosInstance.post(this.endpoints.UPDATE_BIOMETRIC_STATUS, {
      status: value,
    });
  }

  async getBiometricToken(uniqueId: string) {
    return await axiosInstance.post(this.endpoints.GET_BIOMETRIC_TOKEN, {
      uniqueId,
    });
  }

  async biometricAuth(token: string) {
    const responseData: CompleteSignUpResponse = await baseAxiosInstance.post(
      this.endpoints.SIGN_IN_VIA_BIOMETRIC_TOKEN,
      {
        token,
      },
    );

    this.extractAndSetTokens(responseData);

    return responseData.token;
  }

  async signUp(
    data: SignUpProps,
    channel?: AuthDeliveryChannel,
  ): Promise<SignUpResponse | PendingSignUpResponse> {
    const response: SignUpResponse | PendingSignUpResponse =
      await baseAxiosInstance.post(this.endpoints.SIGN_UP_PATH, {
        ...data,
        ...(channel ? { channel } : {}),
      });

    if (isPendingUserResponse(response)) {
      return response;
    }

    localStorageService().setUnverifiedUserId(response.userId);
    return response;
  }

  async checkPermission() {
    try {
      const response: boolean = await axiosInstance.get(
        this.endpoints.DASHBOARD_PERMISSION_PATH,
        { withCredentials: true },
      );
      return response;
    } catch {
      return false;
    }
  }

  async completeSignUp(phone: string, code: string) {
    const responseData: CompleteSignUpResponse = await baseAxiosInstance.post(
      this.endpoints.COMPLETE_SIGN_UP_PATH,
      { phone, code },
    );
    this.extractAndSetTokens(responseData);
  }

  async signIn(data: SignInProps) {
    const responseData: LoginResponse = await baseAxiosInstance.post(
      this.endpoints.SIGN_IM_PATH,
      data,
    );

    this.extractAndSetTokens(responseData);
  }

  async initPhoneSignIn(
    phone: string,
    channel?: AuthDeliveryChannel,
  ): Promise<InitPhoneSignInResponse> {
    return baseAxiosInstance.post(this.endpoints.PHONE_SIGN_IN_INIT, {
      phone,
      ...(channel ? { channel } : {}),
    });
  }

  async phoneSignInVerify(phone: string, code: string) {
    const responseData: LoginResponse = await baseAxiosInstance.post(
      this.endpoints.PHONE_SIGN_IN_VERIFY,
      { phone, code },
    );

    this.extractAndSetTokens(responseData);

    return responseData;
  }

  /** Resending a sign-in code is the same call as initiating one. */
  async resendSignInCode(
    phone: string,
    channel?: AuthDeliveryChannel,
  ): Promise<InitPhoneSignInResponse> {
    return this.initPhoneSignIn(phone, channel);
  }

  async resetPassword(data: {
    password: string;
    repeatPassword: string;
    token?: string;
    userId?: string;
  }) {
    const response: { id: string } = await baseAxiosInstance.post(
      this.endpoints.RESET_PASSWORD_PATH,
      data,
    );

    if (!response?.id) {
      localStorageService().removeAccessToken();
      localStorageService().removeRefreshToken();
      localStorageService().removeUserId();
    } else {
      localStorageService().setUnverifiedUserId(response.id);
    }

    return response?.id;
  }

  async sendResetPasswordInstruction(data: Record<string, string>) {
    const response: { id: string } = await baseAxiosInstance.post(
      this.endpoints.RESET_PASSWORD_INSTRUCTION_PATH,
      data,
    );

    localStorageService().setUnverifiedUserId(response.id);
  }

  async confirmResetPassword(data: { userId: string; code: string }) {
    await baseAxiosInstance.post(
      this.endpoints.CONFIRM_RESET_PASSWORD_PATH,
      data,
    );

    localStorageService().removeUnverifiedUserId();
  }

  async sendResetPasswordCompareCode(data: {
    phone?: string;
    email?: string;
    code: string;
    password: string;
    repeatPassword: string;
  }) {
    const response: { token: string } = await baseAxiosInstance.post(
      this.endpoints.RESET_PASSWORD_COMPARE_CODE,
      data,
    );

    localStorageService().removeUnverifiedUserId();

    return response.token;
  }

  async changeEmail(email: string) {
    await axiosInstance.post(this.endpoints.CHANGE_CONTACT_INIT, { email });
    localStorageService().setPendingEmail(email);
  }

  async resendCode(
    phone: string,
    channel?: AuthDeliveryChannel,
  ): Promise<AuthCodeDeliveryResult> {
    return baseAxiosInstance.post(this.endpoints.SIGN_UP_RESEND_CODE, {
      phone,
      ...(channel ? { channel } : {}),
    });
  }

  async resendResetPasswordCode(phone: string) {
    await baseAxiosInstance.post(this.endpoints.RE_SEND_RESET_PASSWORD_CODE_PATH, {
      phone,
    });
  }

  async changePhone(newPhone: string) {
    await axiosInstance.post(this.endpoints.CHANGE_CONTACT_INIT, {
      phone: newPhone,
    });
    localStorageService().setPendingPhone(newPhone);
  }

  /** Verify a pending contact-change code for either identifier type. */
  private async verifyContactChange(
    identifier: { phone?: string | null } | { email?: string | null },
    code: string,
  ) {
    await axiosInstance.post(this.endpoints.CHANGE_CONTACT_VERIFY, {
      ...identifier,
      codeNewContact: code,
    });
  }

  async verifyPhoneCode(code: string) {
    const phone = localStorageService().getPendingPhone();
    await this.verifyContactChange({ phone }, code);
    localStorageService().removePendingPhone();
  }

  async verifyEmailCode(code: string) {
    const email = localStorageService().getPendingEmail();
    await this.verifyContactChange({ email }, code);
    localStorageService().removePendingEmail();
  }

  async resendChangeContactCode(phone?: string, email?: string) {
    await axiosInstance.post(this.endpoints.CHANGE_CONTACT_RESEND, {
      phone,
      email,
    });
  }
}

// Shared singleton — prefer this over `new AuthService()` at call sites.
export const authService = new AuthService();
